/**
 * frontend/src/pages/valuation/_TuningPreview.tsx
 *
 * VALUATION-B3 — the live half of the tuning screen: what the ENGINE returns
 * for the current knobs, drivers and benchmark.
 *
 * Every number rendered here arrived from `POST .../recompute`. Nothing is
 * interpolated locally while a request is in flight, because a briefly-wrong
 * multiple on screen is indistinguishable from a right one.
 *
 * The band rail is the visible form of guardrail 2 — "the multiple moves only
 * within the computed range, never outside it". The fence is drawn from the
 * benchmark row; the marker is where the driver score positioned inside it.
 * When a CPA override sits outside that fence the marker is drawn clamped to
 * the rail and labelled out-of-range rather than being drawn off the end,
 * which would read as though the fence had moved.
 */
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Fact, basisLabel } from './_shared'
import { convergenceLabel } from './_convergenceLabel'
import { money, multiple, pct } from '@/utils/format'
import type { ComputedPayload, BasisBlock } from '@/types/valuationB3'

function BandRail({ computed }: { computed: ComputedPayload }) {
  const { low, high } = computed.computed_range
  const span = high - low
  const raw = span > 0 ? ((computed.concluded_multiple - low) / span) * 100 : 50
  const clamped = Math.max(0, Math.min(100, raw))
  const outside = raw < 0 || raw > 100
  return (
    <div className="pt-1">
      <div className="relative h-2 rounded-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200">
        <span
          className={`absolute top-1/2 h-4 w-1 -translate-y-1/2 rounded-full ${
            outside ? 'bg-red-600' : 'bg-[#0d1f3c]'
          }`}
          style={{ left: `calc(${clamped}% - 2px)` }}
          aria-hidden
        />
      </div>
      <div className="mt-1 flex justify-between text-[11px] text-pal-ink-muted">
        <span className="font-mono">{multiple(low)}</span>
        <span>
          {outside ? (
            <span className="text-red-700">
              {multiple(computed.concluded_multiple)} — outside the computed range
            </span>
          ) : (
            <>
              {multiple(computed.concluded_multiple)} · {computed.positioned.position_ordinal}
            </>
          )}
        </span>
        <span className="font-mono">{multiple(high)}</span>
      </div>
    </div>
  )
}

export function TuningPreview({
  computed,
  basis,
  stale,
  error,
  blockers,
  warnings,
  valueKind,
}: {
  computed: ComputedPayload | null
  basis: BasisBlock | null
  /** A request is in flight — the numbers shown are from the previous one. */
  stale: boolean
  error: string | null
  blockers: string[]
  warnings: string[]
  /** The engagement type — a concluded engagement previews a "Concluded value". */
  valueKind?: 'concluded' | 'calculated' | null
}) {
  return (
    <Card className={stale ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
        <CardTitle>Live preview</CardTitle>
        {stale && <span className="text-xs text-pal-ink-muted">recomputing…</span>}
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <p className="rounded border border-pal-error-border bg-pal-error-bg px-3 py-2 text-sm text-pal-error-text">
            {error}
          </p>
        )}

        {!computed && !error && (
          <p className="text-sm text-pal-ink-muted">
            Enter normalized earnings and answer the value-driver questions on
            Intake — the engine recomputes as you go.
          </p>
        )}

        {computed && (
          <>
            <div className="rounded-lg border border-pal-line bg-pal-surface-subtle p-3">
              <p className="text-xs text-pal-ink-muted">
                {valueKind === 'concluded' ? 'Concluded value' : 'Calculated value'}
              </p>
              <p className="text-2xl font-semibold text-pal-navy">
                {money(computed.calculated_value)}
              </p>
              {computed.value_low !== null && computed.value_high !== null && (
                <p className="text-sm text-pal-ink-soft">
                  {money(computed.value_low)} – {money(computed.value_high)}
                </p>
              )}
            </div>

            <div>
              <p className="mb-1 text-xs font-medium text-pal-ink-soft">
                {computed.computed_range.benchmark_label} · {basisLabel(computed.computed_range.basis)}
                {computed.computed_range.provisional && (
                  <span className="ml-2 rounded bg-pal-pill-warning-bg px-1.5 py-0.5 text-pal-pill-warning-text">
                    provisional band
                  </span>
                )}
              </p>
              <BandRail computed={computed} />
            </div>

            <div>
              <Fact
                label="Concluded multiple"
                value={multiple(computed.concluded_multiple)}
                mono
              />
              <Fact label="Multiple source" value={computed.multiple_source_label ?? computed.multiple_source} />
              <Fact
                label="Position in range"
                value={pct(computed.positioned.position_pct, 0)}
                mono
              />
              <Fact label="Convergence" value={convergenceLabel(computed.convergence_status)} />
              {computed.income_value !== null && (
                <Fact label="Income indication" value={money(computed.income_value)} mono />
              )}
              <Fact label="Market indication" value={money(computed.market_value)} mono />
              {computed.asset_floor_value !== null && (
                <Fact label="Asset floor" value={money(computed.asset_floor_value)} mono />
              )}
              {basis?.tuned && (
                <Fact label="Methodology" value="Tuned for this engagement" />
              )}
            </div>

            {computed.clamp_note && (
              <p className="rounded border border-amber-200 bg-pal-warning-bg px-3 py-2 text-xs text-pal-pill-warning-text">
                {computed.clamp_note}
              </p>
            )}

            {computed.override_out_of_range && (
              <p className="rounded border border-pal-error-border bg-pal-error-bg px-3 py-2 text-xs text-pal-error-text">
                The override sits outside the computed range. It is shown here at the
                value you stated and flagged — the preview does not move it. It cannot
                be signed until it is withdrawn or bounded to the range.
              </p>
            )}

            {computed.notes.length > 0 && (
              <ul className="list-disc space-y-1 pl-4 text-xs text-pal-ink-soft">
                {computed.notes.map((n, i) => (
                  <li key={i}>{n}</li>
                ))}
              </ul>
            )}

            <p className="border-t border-gray-100 pt-3 text-xs text-pal-ink-muted">
              {computed.disclosure}
            </p>
          </>
        )}

        {blockers.length > 0 && (
          <div className="rounded border border-amber-200 bg-pal-warning-bg p-3">
            <p className="text-xs font-medium text-pal-pill-warning-text">
              Blocking sign-off ({blockers.length})
            </p>
            <ul className="mt-1 list-disc space-y-1 pl-4 text-xs text-pal-pill-warning-text">
              {blockers.map((b, i) => (
                <li key={i}>{b}</li>
              ))}
            </ul>
          </div>
        )}

        {warnings.length > 0 && (
          <div className="rounded border border-pal-line p-3">
            <p className="text-xs font-medium text-pal-ink-body">
              Warnings — disclosed, not blocking
            </p>
            <ul className="mt-1 list-disc space-y-1 pl-4 text-xs text-pal-ink-soft">
              {warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
